/**
 * Tempo 协议相关常量
 * 用于解析和处理 tempo:// 协议链接
 */

// 从环境变量读取协议名称
export const TEMPO_PROTOCOL_NAME = import.meta.env.VITE_TEMPO_PROTOCOL_NAME || 'tempo';

// 支持的实体类型
export enum TempoEntityType {
  PROJECT = 'project',
  TASK = 'task'
}

// URL 解析正则表达式
export const TEMPO_URL_REGEX = new RegExp(
  `^${TEMPO_PROTOCOL_NAME}://(${Object.values(TempoEntityType).join('|')})/([a-f0-9-]{36})$`,
  'i'
);

// UUID 格式验证正则表达式
export const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// 解析 tempo:// URL 的结果类型
export interface ParsedTempoUrl {
  entityType: TempoEntityType;
  entityId: string;
  isValid: boolean;
}

// 解析 tempo:// URL
export const parseTempoUrl = (url: string): ParsedTempoUrl => {
  const match = url.match(TEMPO_URL_REGEX);

  if (!match) {
    return {
      entityType: TempoEntityType.PROJECT,
      entityId: '',
      isValid: false
    };
  }

  const [, entityType, entityId] = match;

  return {
    entityType: entityType as TempoEntityType,
    entityId,
    isValid: UUID_REGEX.test(entityId)
  };
};

// 检查是否为 tempo:// 协议链接
export const isTempoUrl = (url: string): boolean => {
  return url.startsWith(`${TEMPO_PROTOCOL_NAME}://`);
};

// 任务引用数据结构
export interface TempoTaskReference {
  taskId: string;
  displayText: string;
  isValid: boolean;
}

// 提取文本中的 tempo://task/ 链接
export const extractTempoTaskLinksFromText = (text: string): TempoTaskReference[] => {
  if (!text || typeof text !== 'string') {
    return [];
  }

  const references: TempoTaskReference[] = [];

  // 匹配 markdown 链接格式: [text](tempo://task/uuid)
  const markdownLinkRegex = new RegExp(
    `\\[([^\\]]+)\\]\\(${TEMPO_PROTOCOL_NAME}://task/([a-f0-9-]{36})\\)`,
    'gi'
  );

  let match;
  while ((match = markdownLinkRegex.exec(text)) !== null) {
    const [, displayText, taskId] = match;
    const isValid = UUID_REGEX.test(taskId);

    // 避免重复添加相同的任务
    if (!references.some(ref => ref.taskId === taskId)) {
      references.push({
        taskId,
        displayText: displayText.trim(),
        isValid
      });
    }
  }

  // 也匹配纯 URL 格式: tempo://task/uuid
  const urlRegex = new RegExp(
    `${TEMPO_PROTOCOL_NAME}://task/([a-f0-9-]{36})`,
    'gi'
  );

  while ((match = urlRegex.exec(text)) !== null) {
    const [fullUrl, taskId] = match;
    const isValid = UUID_REGEX.test(taskId);

    // 避免重复添加相同的任务（可能已经通过 markdown 格式添加了）
    if (!references.some(ref => ref.taskId === taskId)) {
      references.push({
        taskId,
        displayText: fullUrl, // 使用完整 URL 作为显示文本
        isValid
      });
    }
  }

  return references.filter(ref => ref.isValid);
};
